import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BdsLabelPill from '../../../../../shared/components/v3/BdsLabelPill/BdsLabelPill';
import BdsTooltip from '../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import EmptyCard from '../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import ExpansiveCard from '../../../../../shared/components/v3/Cards/ExpansiveCard/ExpansiveCard';
import StyledTable from '../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import { COLLABORATORS_ADD_DEPENDENTS_ROUTE } from '../../../../../shared/constants/auth';
import { COLLABORATOR_DETAILS_DEPENDENT_RELATIONSHIP } from '../../../../../shared/constants/collaboratorDetails';
import { ADD_ICON } from '../../../../../shared/constants/images';
import {
  CAN_SEE_DEPENDENTS_CARD_FG,
  CAN_SEE_DEPENDENTS_INSURANCE_START_COLUMN_FG
} from '../../../../../shared/constants/statsig';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { COLLABORATOR_DEPENDENTS_TABLE_COLUMNS } from '../../../../../shared/handlers/tableColumns';
import { IMembersData } from '../../../../../shared/interfaces/businessData';
import { ICollaboratorDependents } from '../../../../../shared/interfaces/collaborators';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { isCurrentDayFromDayOfMonth } from '../../../../../shared/utils/datetime/datetime';

import './DependentsCard.scss';

interface IDependentsCardProps {
  collaborator: IMembersData;
}

const DependentsCard = ({ collaborator }: IDependentsCardProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [dependents, setDependents] = useState<ICollaboratorDependents>();
  const navigate = useNavigate();

  const { company, isMexico } = useContext(CompanyContext);

  const { t } = useTranslation();
  const { client } = useStatsigClient();

  const disableButton = useMemo(() => isMexico && isCurrentDayFromDayOfMonth(25), [isMexico]);

  const canSeeCardFG = useMemo(() => client?.checkGate(CAN_SEE_DEPENDENTS_CARD_FG), [client]);
  const canInsuranceStartFG = useMemo(() => client?.checkGate(CAN_SEE_DEPENDENTS_INSURANCE_START_COLUMN_FG), [client]);

  const setStableColumns = () =>
    canInsuranceStartFG
      ? COLLABORATOR_DEPENDENTS_TABLE_COLUMNS
      : COLLABORATOR_DEPENDENTS_TABLE_COLUMNS.filter((_, index) => index !== 2);

  const fectchDependents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Companies.getCollaboratorDependents(company.id, collaborator.email);
      setDependents(response);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [company.id, collaborator]);

  const redirectToAddDependentsPage = () => {
    navigate(COLLABORATORS_ADD_DEPENDENTS_ROUTE);
  };

  useEffect(() => {
    if (company && collaborator) {
      fectchDependents();
    }
  }, [company, collaborator, fectchDependents]);

  if (!canSeeCardFG) {
    return <></>;
  }

  const renderCardHeader = () => (
    <div className="flex flex-row justify-between w-full">
      <div className="title">{t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.title)}</div>
      <BdsTooltip
        message={t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.addDependentsTooltip)}
        renderTooltip={disableButton}
        position="left"
      >
        <BdsButton
          action={redirectToAddDependentsPage}
          variant="primary"
          size="s"
          iconLeft={ADD_ICON}
          disabled={disableButton}
        >
          {t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.familyAddon)}
        </BdsButton>
      </BdsTooltip>
    </div>
  );

  const renderMainContent = () => {
    if (loading) {
      return (
        <>
          <Skeleton className="hp-skeleton-card" variant="rectangular" animation={false} />
          <Skeleton className="hp-skeleton-card" variant="rectangular" animation={false} />
          <Skeleton className="hp-skeleton-card" variant="rectangular" animation={false} />
        </>
      );
    }

    if (error) {
      return (
        <div className="pl-4 pt-2 pb-14">
          <ErrorCard
            primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
            secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
            action={fectchDependents}
            buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          />
        </div>
      );
    }

    if ((dependents?.dependents?.length ?? 0) === 0) {
      return (
        <div className="pl-4 pt-2 pb-14">
          <EmptyCard
            primaryText={t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.emptyState.title)}
            secondaryText={t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.emptyState.description)}
          />
        </div>
      );
    }

    return dependents?.dependents.map((dependent) => (
      <ExpansiveCard
        key={dependent.id}
        title={dependent.name}
        description={
          <div className="hp-dependents-card-description">
            <BdsLabelPill
              text={
                COLLABORATOR_DETAILS_DEPENDENT_RELATIONSHIP.includes(dependent.relationship)
                  ? t(
                      TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.relationship[
                        dependent.relationship
                      ]
                    )
                  : ''
              }
              variant="accent"
              color="accent-quaternary"
              bold={false}
            />
            {`${dependent.docType.toUpperCase()}: ${dependent.docValue}`}
          </div>
        }
      >
        <div className="hp-dependents-table">
          <StyledTable data={dependent.benefits} columns={setStableColumns()} showShadowHeader={false} />
        </div>
      </ExpansiveCard>
    ));
  };

  return (
    <div
      className="hp-dependents-container flex flex-col p-8 gap-8"
      data-experience="new"
      data-testid="dependents-section"
    >
      <div className="flex flex-col gap-16 items-baseline">
        {loading ? (
          <div className="flex flex-row justify-between w-full">
            <div className="title">{t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.title)}</div>
          </div>
        ) : (
          renderCardHeader()
        )}
        {loading ? (
          <Skeleton className="hp-skeleton-description" variant="rectangular" animation={false} />
        ) : (
          dependents &&
          dependents.dependents.length > 0 && (
            <div className="description">
              {t(TRANSLATION_COLLABORATORS.detail.healthProtection.dependentsCard.description, {
                count: dependents?.dependents.length,
                total: dependents?.dependents.length
              })}
            </div>
          )
        )}
      </div>
      {renderMainContent()}
    </div>
  );
};

export default DependentsCard;
